import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setAssetTokenFormData, updateAssetTokenFormField, clearAssetTokenFormData } from '../../store/slices/settingsSlice';
import SignatoriesModal from './SignatoriesModal';
import './AdminComponents.css';

interface AssetToken {
  id: number;
  tokenSymbol: string;
  assetType: string;
  assetName: string;
  assetValue: number;
  assetLocation: string;
  certificateNumber: string;
  tokenSupply: number;
  backingRatio: number;
  minimumInvestment: number;
  managementFee: number;
  insuranceCoverage: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  description: string;
  legalDocumentation: string;
  currentPrice: number;
  marketCap: number;
  // Blockchain fields
  blockchain?: string;
  contractAddress?: string;
  tokenStandard?: string;
  networkType?: string;
  deploymentStatus?: string;
  deploymentTxHash?: string;
  // Investment tracking fields
  investorCount?: number;
  totalInvestmentAmount?: number;
  // Multi-signature wallet fields
  multiSignatureWalletId?: number;
  hasMultiSignatureWallet?: boolean;
}

interface Signatory {
  userId: number;
  signatoryType: string;
  signatureWeight: number;
}

interface AssetTokenInvestment {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userAvatarBase64?: string;
  userPhone?: string;
  userCountry?: string;
  investmentAmount: number;
  tokensPurchased: number;
  purchasePricePerToken: number;
  currentValue: number;
  profitLoss: number;
  profitLossPercentage: number;
  investmentDate: string;
  status: string;
  transactionHash?: string;
}

const AssetTokens: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getAssetTokens, createAssetToken, updateAssetTokenValue, updateAssetTokenStatus, getAvailableUsers, createPropertyEscrowWallet } = useAdminApiService();
  
  // Debug service methods
  useEffect(() => {
    console.log('Service methods available:', {
      getAssetTokens: typeof getAssetTokens,
      createAssetToken: typeof createAssetToken,
      createPropertyEscrowWallet: typeof createPropertyEscrowWallet,
      getAvailableUsers: typeof getAvailableUsers
    });
  }, [getAssetTokens, createAssetToken, createPropertyEscrowWallet, getAvailableUsers]);
  const [tokens, setTokens] = useState<AssetToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedToken, setSelectedToken] = useState<AssetToken | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Available users for multi-signature wallet signatories
  const [availableUsers, setAvailableUsers] = useState<Array<{id: number, username: string, email: string, firstName: string, lastName: string, role: string}>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Get form data from Redux store
  const assetTokenFormData = useAppSelector(state => state.settings.assetTokenFormData);
  
  // Get admin user info for debugging
  const adminUser = useAppSelector(state => state.auth.adminUser);
  
  // Debug admin user permissions
  useEffect(() => {
    if (adminUser) {
      console.log('Current Admin User:', {
        username: adminUser.username,
        role: adminUser.role,
        permissions: adminUser.permissions,
        hasManageAssetTokens: adminUser.permissions?.includes('MANAGE_ASSET_TOKENS'),
        hasViewAssetTokens: adminUser.permissions?.includes('VIEW_ASSET_TOKENS'),
        isSuperAdmin: adminUser.role === 'SUPER_ADMIN'
      });
    }
  }, [adminUser]);

  // Investment tracking state
  const [showInvestmentsModal, setShowInvestmentsModal] = useState(false);
  const [selectedTokenInvestments, setSelectedTokenInvestments] = useState<AssetTokenInvestment[]>([]);
  const [loadingInvestments, setLoadingInvestments] = useState(false);

  // Investment details modal state
  const [showInvestmentDetailsModal, setShowInvestmentDetailsModal] = useState(false);
  const [selectedInvestment, setSelectedInvestment] = useState<AssetTokenInvestment | null>(null);

  // Debug investments modal state
  useEffect(() => {
    if (showInvestmentsModal) {
      console.log('Modal state:', { 
        loadingInvestments, 
        selectedTokenInvestments: selectedTokenInvestments.length,
        selectedToken: selectedToken?.tokenSymbol 
      });
    }
  }, [showInvestmentsModal, loadingInvestments, selectedTokenInvestments, selectedToken]);
  
  // New token creation state - initialize from Redux or defaults
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newToken, setNewToken] = useState({
    tokenSymbol: assetTokenFormData?.tokenSymbol || '',
    assetType: assetTokenFormData?.assetType || 'GOLD',
    assetName: assetTokenFormData?.assetName || '',
    assetValue: assetTokenFormData?.assetValue || '',
    assetLocation: assetTokenFormData?.assetLocation || '',
    certificateNumber: assetTokenFormData?.certificateNumber || '',
    description: assetTokenFormData?.description || '',
    minimumInvestment: assetTokenFormData?.minimumInvestment || '',
    managementFee: assetTokenFormData?.managementFee || '',
    insuranceCoverage: assetTokenFormData?.insuranceCoverage || '',
    // Blockchain fields
    blockchain: assetTokenFormData?.blockchain || 'ETHEREUM',
    tokenStandard: assetTokenFormData?.tokenStandard || 'ERC-20',
    networkType: assetTokenFormData?.networkType || 'TESTNET',
    deployToBlockchain: assetTokenFormData?.deployToBlockchain || false
  });

  // Update Redux store when form data changes
  useEffect(() => {
    if (showCreateModal) {
      dispatch(setAssetTokenFormData(newToken));
    }
  }, [newToken, showCreateModal, dispatch]);

  // Initialize form data in Redux when modal opens
  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    // If no existing data in Redux, initialize with defaults
    if (!assetTokenFormData) {
      const defaultData = {
        tokenSymbol: '',
        assetType: 'GOLD',
        assetName: '',
        assetValue: '',
        assetLocation: '',
        certificateNumber: '',
        description: '',
        minimumInvestment: '',
        managementFee: '',
        insuranceCoverage: '',
        blockchain: 'ETHEREUM',
        tokenStandard: 'ERC-20',
        networkType: 'TESTNET',
        deployToBlockchain: false
      };
      dispatch(setAssetTokenFormData(defaultData));
      setNewToken(defaultData);
    } else {
      // Use existing data from Redux
      setNewToken(assetTokenFormData);
    }
  };

  // Clear Redux data when modal closes
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    dispatch(clearAssetTokenFormData());
    setNewToken({
      tokenSymbol: '',
      assetType: 'GOLD',
      assetName: '',
      assetValue: '',
      assetLocation: '',
      certificateNumber: '',
      description: '',
      minimumInvestment: '',
      managementFee: '',
      insuranceCoverage: '',
      blockchain: 'ETHEREUM',
      tokenStandard: 'ERC-20',
      networkType: 'TESTNET',
      deployToBlockchain: false
    });
  };

  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAssetTokens();
      if (response.success && response.data) {
        const tokensData = response.data as AssetToken[];
        
        // For each token, check if it has a multi-signature wallet
        const tokensWithMultiSigInfo = await Promise.all(
          tokensData.map(async (token) => {
            if (token.assetType === 'PROPERTY') {
              try {
                // Check if this token has a multi-signature wallet
                const multiSigResponse = await fetch(
                  `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/admin/multi-signature-wallets/by-asset-token/${token.id}`,
                  { credentials: 'include' }
                );
                
                if (multiSigResponse.ok) {
                  const multiSigData = await multiSigResponse.json();
                  if (multiSigData.status === 'success' && multiSigData.data) {
                    return {
                      ...token,
                      multiSignatureWalletId: multiSigData.data.id,
                      hasMultiSignatureWallet: true
                    };
                  }
                }
              } catch (error) {
                console.error(`Error checking multi-sig wallet for token ${token.id}:`, error);
              }
            }
            
            return {
              ...token,
              multiSignatureWalletId: null,
              hasMultiSignatureWallet: false
            };
          })
        );
        
        setTokens(tokensWithMultiSigInfo);
      } else {
        setError('Failed to load asset tokens');
      }
    } catch (err) {
      setError('Failed to load asset tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToken = (token: AssetToken) => {
    setSelectedToken(token);
    setEditValue(token.assetValue.toString());
    setEditStatus(token.status);
    setShowEditModal(true);
  };

  const handleUpdateToken = async () => {
    if (!selectedToken) return;

    try {
      setIsUpdating(true);
      
      // Update asset value
      if (parseFloat(editValue) !== selectedToken.assetValue) {
        await updateAssetTokenValue(selectedToken.id, parseFloat(editValue));
      }
      
      // Update status
      if (editStatus !== selectedToken.status) {
        await updateAssetTokenStatus(selectedToken.id, editStatus);
      }
      
      setShowEditModal(false);
      setSelectedToken(null);
      setEditValue('');
      setEditStatus('');
      
      // Reload tokens
      await loadTokens();
      
    } catch (err) {
      setError('Failed to update token');
      console.error('Error updating token:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateToken = async () => {
    try {
      setIsCreating(true);
      
      // Validate required fields
      if (!newToken.tokenSymbol || !newToken.assetName || !newToken.assetValue) {
        setError('Please fill in all required fields');
        return;
      }

      // Create token payload
      const tokenPayload = {
        tokenSymbol: newToken.tokenSymbol.toUpperCase(),
        assetType: newToken.assetType,
        assetName: newToken.assetName,
        assetValue: parseFloat(newToken.assetValue),
        assetLocation: newToken.assetLocation || 'Unknown',
        certificateNumber: newToken.certificateNumber || 'N/A',
        description: newToken.description || '',
        minimumInvestment: parseFloat(newToken.minimumInvestment) || 100,
        managementFee: parseFloat(newToken.managementFee) || 0.5,
        insuranceCoverage: parseFloat(newToken.insuranceCoverage) || 0,
        status: 'ACTIVE',
        // Blockchain fields
        blockchain: newToken.blockchain,
        tokenStandard: newToken.tokenStandard,
        networkType: newToken.networkType,
        deployToBlockchain: newToken.deployToBlockchain
      };

      const response = await createAssetToken(tokenPayload);
      
      if (response.success) {
        console.log('Token created successfully:', response.data);
        // Clear form data from Redux and close modal
        dispatch(clearAssetTokenFormData());
        setShowCreateModal(false);
        
        // Reset form
        setNewToken({
          tokenSymbol: '',
          assetType: 'GOLD',
          assetName: '',
          assetValue: '',
          assetLocation: '',
          certificateNumber: '',
          description: '',
          minimumInvestment: '',
          managementFee: '',
          insuranceCoverage: '',
          blockchain: 'ETHEREUM',
          tokenStandard: 'ERC-20',
          networkType: 'TESTNET',
          deployToBlockchain: false
        });
        
        // Reload tokens
        await loadTokens();
        
      } else {
        setError('Failed to create token');
        console.error('Error creating token:', response.error);
      }
      
    } catch (err) {
      setError('Failed to create token');
      console.error('Error creating token:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    const newValue = field === 'deployToBlockchain' ? value === 'true' : value;
    setNewToken(prev => ({
      ...prev,
      [field]: newValue
    }));
    
    // Also update Redux store immediately
    dispatch(updateAssetTokenFormField({ 
      field: field as keyof typeof newToken, 
      value: newValue 
    }));
  };

  const handleDeployToken = async (tokenId: number) => {
    try {
      // Simulate deployment process
      console.log(`Deploying token ${tokenId} to blockchain...`);
      
      // Update token status to DEPLOYING
      await updateAssetTokenStatus(tokenId, 'DEPLOYING');
      
      // Simulate deployment delay
      setTimeout(async () => {
        try {
          // Update to DEPLOYED with mock contract address
          const mockContractAddress = `0x${Math.random().toString(16).substr(2, 40)}`;
          const mockTxHash = `0x${Math.random().toString(16).substr(2, 64)}`;
          
          console.log(`Token ${tokenId} deployed successfully!`);
          console.log(`Contract Address: ${mockContractAddress}`);
          console.log(`Transaction Hash: ${mockTxHash}`);
          
          // In a real implementation, you would update the token with actual deployment data
          // For now, we'll just update the status
          await updateAssetTokenStatus(tokenId, 'DEPLOYED');
          
          // Reload tokens to show updated status
          await loadTokens();
          
        } catch (error) {
          console.error('Deployment failed:', error);
          await updateAssetTokenStatus(tokenId, 'FAILED');
        }
      }, 3000);
      
    } catch (error) {
      console.error('Error deploying token:', error);
    }
  };

  const loadInvestments = async (tokenId: number) => {
    setLoadingInvestments(true);
    console.log('Loading investments for token ID:', tokenId);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/admin/asset-token-investments/token/${tokenId}`, {
        credentials: 'include'
      });
      
      console.log('Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Investment data received:', data);
        if (data.status === 'success') {
          console.log('Setting investments:', data.data);
          setSelectedTokenInvestments(data.data);
        } else {
          console.error('Failed to load investments:', data.message);
        }
      } else {
        console.error('Failed to load investments:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading investments:', error);
    } finally {
      setLoadingInvestments(false);
    }
  };

  const handleViewInvestments = async (token: AssetToken) => {
    console.log('Opening investments modal for token:', token);
    setSelectedToken(token);
    setShowInvestmentsModal(true);
    await loadInvestments(token.id);
  };

  const handleViewInvestmentDetails = (investment: AssetTokenInvestment) => {
    setSelectedInvestment(investment);
    setShowInvestmentDetailsModal(true);
  };

  // Enhanced color system for asset types
  const getAssetTypeColors = (assetType: string) => {
    const colorMap: { [key: string]: { bg: string; text: string; border?: string } } = {
      'GOLD': { bg: '#ffd700', text: '#000000', border: '#b8860b' },
      'SILVER': { bg: '#c0c0c0', text: '#000000', border: '#a0a0a0' },
      'DIAMOND': { bg: '#b9f2ff', text: '#000000', border: '#87ceeb' },
      'CAR': { bg: '#ff6b6b', text: '#ffffff', border: '#e74c3c' },
      'PROPERTY': { bg: '#4ecdc4', text: '#ffffff', border: '#26a69a' },
      'FILM': { bg: '#7e57c2', text: '#ffffff', border: '#5e35b1' },
      'CRYPTO': { bg: '#f7931a', text: '#ffffff', border: '#e67e22' },
      'STOCK': { bg: '#00d4aa', text: '#ffffff', border: '#00b894' },
      'BOND': { bg: '#007bff', text: '#ffffff', border: '#0056b3' },
      'COMMODITY': { bg: '#fd7e14', text: '#ffffff', border: '#e55a00' },
      'REAL_ESTATE': { bg: '#8e44ad', text: '#ffffff', border: '#6c3483' },
      'ART': { bg: '#e91e63', text: '#ffffff', border: '#c2185b' },
      'WINE': { bg: '#8b0000', text: '#ffffff', border: '#660000' },
      'WATCH': { bg: '#2c3e50', text: '#ffffff', border: '#1a252f' },
      'JEWELRY': { bg: '#ff69b4', text: '#ffffff', border: '#ff1493' },
      'COLLECTIBLE': { bg: '#ff8c00', text: '#ffffff', border: '#ff7f00' }
    };
    
    return colorMap[assetType] || { bg: '#6c757d', text: '#ffffff', border: '#495057' };
  };

  const getAssetTypeColor = (assetType: string) => {
    return getAssetTypeColors(assetType).bg;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return '#28a745';
      case 'SUSPENDED': return '#ffc107';
      case 'REDEEMED': return '#dc3545';
      case 'UNDER_AUDIT': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const getSignatoryTypeColor = (type: string) => {
    switch (type) {
      case 'ADMIN': return '#dc3545';
      case 'PROPERTY_OWNER': return '#28a745';
      case 'BUYER': return '#007bff';
      case 'ESCROW_AGENT': return '#ffc107';
      case 'LEGAL_REPRESENTATIVE': return '#6f42c1';
      case 'INVESTOR': return '#fd7e14';
      default: return '#6c757d';
    }
  };

  // Multi-signature wallet state
  const [showMultiSignatureModal, setShowMultiSignatureModal] = useState(false);
  const [selectedTokenForMultiSig, setSelectedTokenForMultiSig] = useState<AssetToken | null>(null);
  const [multiSignatureFormData, setMultiSignatureFormData] = useState<{
    blockchain: string;
    requiredSignatures: number;
    currency: string;
    escrowTerms: string;
    releaseConditions: string;
    signatories: Signatory[];
  }>({
    blockchain: 'ETHEREUM',
    requiredSignatures: 2,
    currency: 'USD',
    escrowTerms: '',
    releaseConditions: '',
    signatories: [
      { userId: 1, signatoryType: 'ADMIN', signatureWeight: 1 },
      { userId: 2, signatoryType: 'PROPERTY_OWNER', signatureWeight: 1 }
    ]
  });
  const [isCreatingMultiSig, setIsCreatingMultiSig] = useState(false);

  // Multi-signature wallet viewing state
  const [showViewMultiSigModal, setShowViewMultiSigModal] = useState(false);
  const [selectedMultiSigWallet, setSelectedMultiSigWallet] = useState<any>(null);
  const [loadingMultiSigWallet, setLoadingMultiSigWallet] = useState(false);

  // Multi-signature wallet signatories state
  const [showSignatoriesModal, setShowSignatoriesModal] = useState(false);
  const [walletSignatories, setWalletSignatories] = useState<any[]>([]);
  const [loadingSignatories, setLoadingSignatories] = useState(false);

  const handleCreateMultiSignatureWallet = async () => {
    console.log('=== handleCreateMultiSignatureWallet called ===');
    console.log('selectedTokenForMultiSig:', selectedTokenForMultiSig);
    console.log('multiSignatureFormData:', multiSignatureFormData);
    
    if (!selectedTokenForMultiSig) {
      console.error('No token selected for multi-signature wallet');
      return;
    }

    // Validate signatories have valid user IDs
    const invalidSignatories = multiSignatureFormData.signatories.filter(sig => 
      typeof sig.userId !== 'number' || sig.userId <= 0
    );
    
    console.log('Invalid signatories:', invalidSignatories);
    
    if (invalidSignatories.length > 0) {
      setError('All signatories must have a valid user selected. Please select a user for each signatory.');
      return;
    }

    try {
      console.log('Starting multi-signature wallet creation...');
      setIsCreatingMultiSig(true);
      
      const requestData = {
        ...multiSignatureFormData,
        assetTokenId: selectedTokenForMultiSig.id,
        walletType: 'PROPERTY_ESCROW'
      };
      
      console.log('Request data:', requestData);
      
      const response = await createPropertyEscrowWallet(requestData);
      
      console.log('Response received:', response);

      if (response.success) {
        alert('Multi-signature wallet created successfully!');
        setShowMultiSignatureModal(false);
        setSelectedTokenForMultiSig(null);
        setMultiSignatureFormData({
          blockchain: 'ETHEREUM',
          requiredSignatures: 2,
          currency: 'USD',
          escrowTerms: '',
          releaseConditions: '',
          signatories: [
            { userId: 1, signatoryType: 'ADMIN', signatureWeight: 1 },
            { userId: 2, signatoryType: 'PROPERTY_OWNER', signatureWeight: 1 }
          ]
        });
        await loadTokens(); // Refresh the tokens list
      } else {
        // Use error property from ApiResponse type
        setError(response.error || 'Failed to create multi-signature wallet');
      }
    } catch (err) {
      console.error('Error in handleCreateMultiSignatureWallet:', err);
      setError('Failed to create multi-signature wallet');
      console.error('Error creating multi-signature wallet:', err);
    } finally {
      setIsCreatingMultiSig(false);
    }
  };

  const handleOpenMultiSignatureModal = (token: AssetToken) => {
    if (token.assetType !== 'PROPERTY') {
      alert('Multi-signature wallets are only available for property assets');
      return;
    }
    
    setSelectedTokenForMultiSig(token);
    setShowMultiSignatureModal(true);
    loadAvailableUsers();
  };

  const loadAvailableUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await getAvailableUsers();
      if (response.success && response.data) {
        setAvailableUsers(response.data);
      } else {
        console.error('Failed to load available users:', response.error);
      }
    } catch (err) {
      console.error('Error loading available users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleMultiSignatureInputChange = (field: string, value: any) => {
    setMultiSignatureFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSignatoryChange = (index: number, field: string, value: any) => {
    setMultiSignatureFormData(prev => ({
      ...prev,
      signatories: prev.signatories.map((sig, i) => 
        i === index ? { 
          ...sig, 
          [field]: field === 'userId' ? parseInt(value) || 0 : 
                   field === 'signatureWeight' ? parseInt(value) || 1 : value 
        } : sig
      )
    }));
  };

  const addSignatory = () => {
    setMultiSignatureFormData(prev => ({
      ...prev,
      signatories: [...prev.signatories, { userId: 3, signatoryType: 'INVESTOR', signatureWeight: 1 }]
    }));
  };

  const removeSignatory = (index: number) => {
    setMultiSignatureFormData(prev => ({
      ...prev,
      signatories: prev.signatories.filter((_, i) => i !== index)
    }));
  };

  const handleViewMultiSignatureWallet = async (token: AssetToken) => {
    if (!token.multiSignatureWalletId) {
      alert('No multi-signature wallet found for this token');
      return;
    }

    setLoadingMultiSigWallet(true);
    setSelectedToken(token);
    setShowViewMultiSigModal(true);

    try {
      // Fetch multi-signature wallet details
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/admin/multi-signature-wallets/${token.multiSignatureWalletId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setSelectedMultiSigWallet(data.data);
        } else {
          console.error('Failed to load multi-signature wallet:', data.message);
          alert('Failed to load multi-signature wallet details');
        }
      } else {
        console.error('Failed to load multi-signature wallet:', response.statusText);
        alert('Failed to load multi-signature wallet details');
      }
    } catch (error) {
      console.error('Error loading multi-signature wallet:', error);
      alert('Error loading multi-signature wallet details');
    } finally {
      setLoadingMultiSigWallet(false);
    }
  };

  const handleManageSignatories = async (walletId: number) => {
    setLoadingSignatories(true);
    setShowSignatoriesModal(true);

    try {
      // Fetch signatories for the wallet
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/admin/multi-signature-wallets/${walletId}/signatories`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setWalletSignatories(data.data);
        } else {
          console.error('Failed to load signatories:', data.message);
          alert('Failed to load signatories');
        }
      } else {
        console.error('Failed to load signatories:', response.statusText);
        alert('Failed to load signatories');
      }
    } catch (error) {
      console.error('Error loading signatories:', error);
      alert('Error loading signatories');
    } finally {
      setLoadingSignatories(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading asset tokens...</p>
      </div>
    );
  }

  return (
    <div className="admin-content">
      <div className="admin-header">
        <h2>Asset-Backed Tokens Management</h2>
        <div className="admin-actions">
          <button 
            onClick={handleOpenCreateModal}
            className="admin-button success"
          >
            ➕ Create New Token
          </button>
          <button 
            onClick={loadTokens}
            className="admin-button"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-error-message">
          {error}
        </div>
      )}

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Token Symbol</th>
              <th>Asset Type</th>
              <th>Asset Name</th>
              <th>Asset Value</th>
              <th>Token Price</th>
              <th>Market Cap</th>
              <th>Blockchain</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((token) => (
              <tr key={token.id}>
                <td>
                  <strong>{token.tokenSymbol}</strong>
                </td>
                <td>
                  <span 
                    className="asset-type-badge"
                    style={{ 
                      backgroundColor: getAssetTypeColors(token.assetType).bg,
                      color: getAssetTypeColors(token.assetType).text,
                      border: `1px solid ${getAssetTypeColors(token.assetType).border || getAssetTypeColors(token.assetType).bg}`,
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      display: 'inline-block',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {token.assetType}
                  </span>
                </td>
                <td>{token.assetName}</td>
                <td>${token.assetValue.toLocaleString()}</td>
                <td>${token.currentPrice.toFixed(6)}</td>
                <td>${token.marketCap.toLocaleString()}</td>
                <td>
                  {token.blockchain ? (
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '12px' }}>
                        {token.blockchain}
                      </div>
                      {token.deploymentStatus && (
                        <div style={{ 
                          fontSize: '10px', 
                          color: token.deploymentStatus === 'DEPLOYED' ? '#28a745' : 
                                 token.deploymentStatus === 'DEPLOYING' ? '#ffc107' : 
                                 token.deploymentStatus === 'FAILED' ? '#dc3545' : '#6c757d'
                        }}>
                          {token.deploymentStatus}
                        </div>
                      )}
                      {token.contractAddress && (
                        <div style={{ fontSize: '10px', color: '#6b7280' }}>
                          {token.contractAddress.substring(0, 8)}...
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#6c757d', fontSize: '12px' }}>Not deployed</span>
                  )}
                </td>
                <td>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(token.status) }}
                  >
                    {token.status}
                  </span>
                </td>
                <td>
                  <button
                    onClick={() => handleEditToken(token)}
                    className="admin-button"
                  >
                    ✏️ Edit
                  </button>
                  {token.deploymentStatus !== 'DEPLOYED' && token.blockchain && (
                    <button
                      onClick={() => handleDeployToken(token.id)}
                      className="admin-button success"
                      style={{ marginLeft: '8px' }}
                    >
                      🚀 Deploy
                    </button>
                  )}
                  {token.assetType === 'PROPERTY' && !token.hasMultiSignatureWallet && (
                    <button
                      onClick={() => handleOpenMultiSignatureModal(token)}
                      className="admin-button warning"
                      style={{ marginLeft: '8px' }}
                    >
                      🔐 Create Multi-Sig
                    </button>
                  )}
                  {token.hasMultiSignatureWallet && (
                    <button
                      onClick={() => handleViewMultiSignatureWallet(token)}
                      className="admin-button info"
                      style={{ marginLeft: '8px' }}
                    >
                      🔐 View Multi-Sig
                    </button>
                  )}
                  <button
                    onClick={() => handleViewInvestments(token)}
                    className="admin-button info"
                    style={{ marginLeft: '8px' }}
                  >
                    👥 View Investors
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedToken && (
        <div className="modal-overlay">
          <div className="user-details-modal">
            <div className="modal-header">
              <h3>Edit Asset Token</h3>
              <button 
                className="modal-close" 
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedToken(null);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Token Symbol</label>
                <input 
                  type="text" 
                  value={selectedToken.tokenSymbol} 
                  disabled 
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Asset Value (USD)</label>
                <input 
                  type="number" 
                  value={editValue} 
                  onChange={(e) => setEditValue(e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select 
                  value={editStatus} 
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="form-select"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="SUSPENDED">Suspended</option>
                  <option value="REDEEMED">Redeemed</option>
                  <option value="UNDER_AUDIT">Under Audit</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <div className="action-section">
                <button
                  onClick={handleUpdateToken}
                  disabled={isUpdating}
                  className="action-btn success"
                >
                  {isUpdating ? 'Updating...' : 'Update Token'}
                </button>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedToken(null);
                  }}
                  className="action-btn secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create New Token Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="user-details-modal">
            <div className="modal-header">
              <h3>Create New Asset Token</h3>
              <button 
                className="modal-close" 
                onClick={handleCloseCreateModal}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Token Symbol *</label>
                <input 
                  type="text" 
                  value={newToken.tokenSymbol} 
                  onChange={(e) => handleInputChange('tokenSymbol', e.target.value)}
                  className="form-input"
                  placeholder="e.g., GOLD-TOKEN-002"
                />
              </div>

              <div className="form-group">
                <label>Asset Type *</label>
                <select 
                  value={newToken.assetType} 
                  onChange={(e) => handleInputChange('assetType', e.target.value)}
                  className="form-select"
                >
                  <option value="GOLD">Gold</option>
                  <option value="SILVER">Silver</option>
                  <option value="DIAMOND">Diamond</option>
                  <option value="CAR">Car</option>
                  <option value="PROPERTY">Property</option>
                  <option value="FILM">Film</option>
                </select>
              </div>

              <div className="form-group">
                <label>Asset Name *</label>
                <input 
                  type="text" 
                  value={newToken.assetName} 
                  onChange={(e) => handleInputChange('assetName', e.target.value)}
                  className="form-input"
                  placeholder="e.g., Gold Bar 2kg"
                />
              </div>

              <div className="form-group">
                <label>Asset Value (USD) *</label>
                <input 
                  type="number" 
                  value={newToken.assetValue} 
                  onChange={(e) => handleInputChange('assetValue', e.target.value)}
                  className="form-input"
                  placeholder="e.g., 130000"
                />
              </div>

              <div className="form-group">
                <label>Asset Location</label>
                <input 
                  type="text" 
                  value={newToken.assetLocation} 
                  onChange={(e) => handleInputChange('assetLocation', e.target.value)}
                  className="form-input"
                  placeholder="e.g., London, UK"
                />
              </div>

              <div className="form-group">
                <label>Certificate Number</label>
                <input 
                  type="text" 
                  value={newToken.certificateNumber} 
                  onChange={(e) => handleInputChange('certificateNumber', e.target.value)}
                  className="form-input"
                  placeholder="e.g., CERT-2024-001"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea 
                  value={newToken.description} 
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="form-input"
                  placeholder="Asset description..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Minimum Investment (USD)</label>
                <input 
                  type="number" 
                  value={newToken.minimumInvestment} 
                  onChange={(e) => handleInputChange('minimumInvestment', e.target.value)}
                  className="form-input"
                  placeholder="e.g., 100"
                />
              </div>

              <div className="form-group">
                <label>Management Fee (%)</label>
                <input 
                  type="number" 
                  value={newToken.managementFee} 
                  onChange={(e) => handleInputChange('managementFee', e.target.value)}
                  className="form-input"
                  placeholder="e.g., 0.5"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label>Insurance Coverage (USD)</label>
                <input 
                  type="number" 
                  value={newToken.insuranceCoverage} 
                  onChange={(e) => handleInputChange('insuranceCoverage', e.target.value)}
                  className="form-input"
                  placeholder="e.g., 10000"
                />
              </div>

              {/* Blockchain Configuration Section */}
              <div style={{ 
                borderTop: '1px solid #e5e7eb', 
                marginTop: '20px', 
                paddingTop: '20px' 
              }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>Blockchain Configuration</h4>
                
                <div className="form-group">
                  <label>Blockchain *</label>
                  <select 
                    value={newToken.blockchain} 
                    onChange={(e) => handleInputChange('blockchain', e.target.value)}
                    className="form-select"
                  >
                    <option value="ETHEREUM">Ethereum</option>
                    <option value="POLYGON">Polygon</option>
                    <option value="BINANCE_SMART_CHAIN">Binance Smart Chain</option>
                    <option value="SOLANA">Solana</option>
                    <option value="CARDANO">Cardano</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Token Standard *</label>
                  <select 
                    value={newToken.tokenStandard} 
                    onChange={(e) => handleInputChange('tokenStandard', e.target.value)}
                    className="form-select"
                  >
                    <option value="ERC-20">ERC-20 (Fungible)</option>
                    <option value="ERC-721">ERC-721 (NFT)</option>
                    <option value="BEP-20">BEP-20 (BSC)</option>
                    <option value="SPL">SPL (Solana)</option>
                    <option value="CIP-25">CIP-25 (Cardano)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Network Type *</label>
                  <select 
                    value={newToken.networkType} 
                    onChange={(e) => handleInputChange('networkType', e.target.value)}
                    className="form-select"
                  >
                    <option value="TESTNET">Testnet (Development)</option>
                    <option value="MAINNET">Mainnet (Production)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Deploy to Blockchain</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input 
                      type="checkbox" 
                      checked={newToken.deployToBlockchain} 
                      onChange={(e) => handleInputChange('deployToBlockchain', e.target.checked.toString())}
                      style={{ width: 'auto' }}
                    />
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>
                      Deploy smart contract to blockchain immediately
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <div className="action-section">
                <button
                  onClick={handleCreateToken}
                  disabled={isCreating}
                  className="action-btn success"
                >
                  {isCreating ? 'Creating...' : 'Create Token'}
                </button>
                <button
                  onClick={handleCloseCreateModal}
                  className="action-btn secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Investments Modal */}
      {showInvestmentsModal && selectedToken && (
        <div className="modal-overlay">
          <div className="user-details-modal" style={{ maxWidth: '900px', maxHeight: '80vh' }}>
            <div className="modal-header">
              <h3>Investors in {selectedToken.tokenSymbol}</h3>
              <button 
                className="modal-close" 
                onClick={() => {
                  setShowInvestmentsModal(false);
                  setSelectedToken(null);
                  setSelectedTokenInvestments([]);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {loadingInvestments ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div className="loading-spinner"></div>
                  <p>Loading investors...</p>
                </div>
              ) : selectedTokenInvestments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                  <p>No investors found for this token.</p>
                </div>
              ) : (
                <div className="investments-table-container">
                  <table className="admin-table" style={{ 
                    fontSize: '11px', 
                    width: '100%',
                    tableLayout: 'fixed'
                  }}>
                    <thead>
                      <tr>
                        <th style={{ width: '25%', minWidth: '200px' }}>Investor</th>
                        <th style={{ width: '12%', minWidth: '100px' }}>Amount</th>
                        <th style={{ width: '15%', minWidth: '120px' }}>Tokens</th>
                        <th style={{ width: '12%', minWidth: '100px' }}>Price</th>
                        <th style={{ width: '12%', minWidth: '100px' }}>Value</th>
                        <th style={{ width: '12%', minWidth: '100px' }}>P/L</th>
                        <th style={{ width: '12%', minWidth: '100px' }}>Date</th>
                        <th style={{ width: '10%', minWidth: '80px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTokenInvestments.map((investment) => (
                        <tr key={investment.id}>
                          <td>
                            <div className="user-info">
                              {investment.userAvatarBase64 ? (
                                <img 
                                  src={`data:image/jpeg;base64,${investment.userAvatarBase64}`}
                                  alt="Avatar"
                                  className="user-avatar"
                                />
                              ) : (
                                <div className="user-avatar" style={{
                                  backgroundColor: '#e5e7eb',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '10px',
                                  color: '#6b7280'
                                }}>
                                  {investment.userFirstName?.charAt(0) || 'U'}
                                </div>
                              )}
                              <div className="user-details">
                                <div className="user-name">
                                  {investment.userFirstName} {investment.userLastName}
                                </div>
                                <div className="user-email">
                                  {investment.userEmail}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="numeric-cell">
                            ${investment.investmentAmount.toLocaleString()}
                          </td>
                          <td className="numeric-cell">
                            {investment.tokensPurchased.toFixed(2)}
                          </td>
                          <td className="numeric-cell">
                            ${investment.purchasePricePerToken.toFixed(4)}
                          </td>
                          <td className="numeric-cell">
                            ${investment.currentValue.toLocaleString()}
                          </td>
                          <td>
                            <div className={`profit-loss ${investment.profitLoss >= 0 ? 'positive' : 'negative'}`}>
                              {investment.profitLoss >= 0 ? '+' : ''}${investment.profitLoss.toFixed(2)}
                              <br />
                              <span style={{ fontSize: '9px' }}>
                                ({investment.profitLossPercentage.toFixed(1)}%)
                              </span>
                            </div>
                          </td>
                          <td className="date-cell">
                            {new Date(investment.investmentDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </td>
                          <td>
                            <button
                              onClick={() => handleViewInvestmentDetails(investment)}
                              className="admin-button info"
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                            >
                              📋 Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <div className="action-section">
                <button
                  onClick={() => {
                    setShowInvestmentsModal(false);
                    setSelectedToken(null);
                    setSelectedTokenInvestments([]);
                  }}
                  className="action-btn secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Investment Details Modal */}
      {showInvestmentDetailsModal && selectedInvestment && selectedToken && (
        <div className="modal-overlay">
          <div className="user-details-modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Investment Details - {selectedToken.tokenSymbol}</h3>
              <button 
                className="modal-close" 
                onClick={() => {
                  setShowInvestmentDetailsModal(false);
                  setSelectedInvestment(null);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {/* Investment Summary Section */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '16px' }}>
                  Investment Summary
                </h4>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '16px',
                  backgroundColor: '#f9fafb',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Asset Token
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                      {selectedToken.tokenSymbol} - {selectedToken.assetName}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Investment Status
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: '600',
                      color: selectedInvestment.status === 'ACTIVE' ? '#10b981' : 
                             selectedInvestment.status === 'SOLD' ? '#ef4444' : '#6b7280'
                    }}>
                      {selectedInvestment.status}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Investment Amount
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                      ${selectedInvestment.investmentAmount.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Tokens Owned
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                      {selectedInvestment.tokensPurchased.toFixed(6)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Purchase Price
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                      ${selectedInvestment.purchasePricePerToken.toFixed(6)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Current Value
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                      ${selectedInvestment.currentValue.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Profit/Loss
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: '600',
                      color: selectedInvestment.profitLoss >= 0 ? '#10b981' : '#ef4444'
                    }}>
                      {selectedInvestment.profitLoss >= 0 ? '+' : ''}${selectedInvestment.profitLoss.toFixed(2)}
                      <br />
                      <span style={{ fontSize: '12px' }}>
                        ({selectedInvestment.profitLossPercentage.toFixed(2)}%)
                      </span>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Investment Date
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                      {new Date(selectedInvestment.investmentDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Blockchain Transaction Details Section */}
              <div>
                <h4 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '16px' }}>
                  Blockchain Transaction Details
                </h4>
                {selectedInvestment.transactionHash ? (
                  <div style={{ 
                    backgroundColor: '#f9fafb',
                    padding: '16px',
                    borderRadius: '8px'
                  }}>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        Transaction Hash
                      </div>
                      <div style={{ 
                        fontSize: '13px', 
                        fontFamily: 'monospace',
                        backgroundColor: '#ffffff',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb',
                        wordBreak: 'break-all'
                      }}>
                        {selectedInvestment.transactionHash}
                      </div>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        Blockchain Network
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                        {selectedToken.blockchain || 'Unknown'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        Contract Address
                      </div>
                      <div style={{ 
                        fontSize: '13px', 
                        fontFamily: 'monospace',
                        backgroundColor: '#ffffff',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb',
                        wordBreak: 'break-all'
                      }}>
                        {selectedToken.contractAddress || 'Not deployed'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    backgroundColor: '#f9fafb',
                    padding: '24px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <div style={{ 
                      fontSize: '48px', 
                      color: '#d1d5db',
                      marginBottom: '16px'
                    }}>
                      🔗
                    </div>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      No Blockchain Data
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#6b7280'
                    }}>
                      This investment was made before blockchain integration was enabled. 
                      Future investments will include detailed blockchain transaction information.
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <div className="action-section">
                <button
                  onClick={() => {
                    setShowInvestmentDetailsModal(false);
                    setSelectedInvestment(null);
                  }}
                  className="action-btn secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Signature Wallet Creation Modal */}
      {showMultiSignatureModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="user-details-modal">
              <div className="modal-header">
                <h3>Create Multi-Signature Wallet for Property Asset</h3>
                <button 
                  className="modal-close" 
                  onClick={() => setShowMultiSignatureModal(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                {selectedTokenForMultiSig && (
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px',
                  }}>
                    <h4 style={{ margin: '0 0 12px 0' }}>Property Asset Details</h4>
                    <div><strong>Token:</strong> {selectedTokenForMultiSig.tokenSymbol}</div>
                    <div><strong>Asset:</strong> {selectedTokenForMultiSig.assetName}</div>
                    <div><strong>Value:</strong> ${selectedTokenForMultiSig.assetValue?.toLocaleString()}</div>
                  </div>
                )}

                <div className="form-group">
                  <label>Blockchain *</label>
                  <select 
                    value={multiSignatureFormData.blockchain} 
                    onChange={(e) => handleMultiSignatureInputChange('blockchain', e.target.value)}
                    className="form-select"
                  >
                    <option value="ETHEREUM">Ethereum</option>
                    <option value="POLYGON">Polygon</option>
                    <option value="BINANCE_SMART_CHAIN">Binance Smart Chain</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Required Signatures *</label>
                  <input 
                    type="number" 
                    value={multiSignatureFormData.requiredSignatures} 
                    onChange={(e) => handleMultiSignatureInputChange('requiredSignatures', parseInt(e.target.value))}
                    className="form-input"
                    min="2"
                    max="10"
                  />
                </div>

                <div className="form-group">
                  <label>Currency *</label>
                  <select 
                    value={multiSignatureFormData.currency} 
                    onChange={(e) => handleMultiSignatureInputChange('currency', e.target.value)}
                    className="form-select"
                  >
                    <option value="USD">USD</option>
                    <option value="ETH">ETH</option>
                    <option value="USDC">USDC</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Escrow Terms</label>
                  <textarea 
                    value={multiSignatureFormData.escrowTerms} 
                    onChange={(e) => handleMultiSignatureInputChange('escrowTerms', e.target.value)}
                    className="form-input"
                    rows={3}
                    placeholder="Terms and conditions for the escrow..."
                  />
                </div>

                <div className="form-group">
                  <label>Release Conditions</label>
                  <textarea 
                    value={multiSignatureFormData.releaseConditions} 
                    onChange={(e) => handleMultiSignatureInputChange('releaseConditions', e.target.value)}
                    className="form-input"
                    rows={3}
                    placeholder="Conditions for releasing funds/assets..."
                  />
                </div>

                <div className="form-group">
                  <label>Signatories *</label>
                  <div style={{ marginBottom: '10px' }}>
                    <button 
                      type="button" 
                      onClick={addSignatory}
                      className="admin-button"
                      style={{ marginBottom: '10px' }}
                    >
                      ➕ Add Signatory
                    </button>
                  </div>
                  
                  {multiSignatureFormData.signatories.map((signatory, index) => (
                    <div key={index} style={{
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '10px',
                      background: '#f9f9f9'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h5 style={{ margin: 0 }}>Signatory {index + 1}</h5>
                        {multiSignatureFormData.signatories.length > 2 && (
                          <button 
                            type="button" 
                            onClick={() => removeSignatory(index)}
                            className="admin-button danger"
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            🗑️ Remove
                          </button>
                        )}
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label>User *</label>
                          {loadingUsers ? (
                            <div style={{ padding: '8px', textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>
                              Loading users...
                            </div>
                          ) : (
                            <select 
                              value={signatory.userId} 
                              onChange={(e) => handleSignatoryChange(index, 'userId', e.target.value)}
                              className="form-select"
                              required
                            >
                              <option value="">Select a user</option>
                              {availableUsers.map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.firstName} {user.lastName} ({user.email}) - {user.role}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div>
                          <label>Type</label>
                          <select 
                            value={signatory.signatoryType} 
                            onChange={(e) => handleSignatoryChange(index, 'signatoryType', e.target.value)}
                            className="form-select"
                          >
                            <option value="ADMIN">Admin</option>
                            <option value="PROPERTY_OWNER">Property Owner</option>
                            <option value="BUYER">Buyer</option>
                            <option value="ESCROW_AGENT">Escrow Agent</option>
                            <option value="LEGAL_REPRESENTATIVE">Legal Representative</option>
                            <option value="INVESTOR">Investor</option>
                          </select>
                        </div>
                        <div>
                          <label>Signature Weight</label>
                          <input 
                            type="number" 
                            value={signatory.signatureWeight} 
                            onChange={(e) => handleSignatoryChange(index, 'signatureWeight', e.target.value)}
                            className="form-input"
                            min="1"
                            max="10"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="modal-actions">
                  <button 
                    onClick={(e) => {
                      console.log('=== Button clicked! ===');
                      console.log('Event:', e);
                      console.log('Button disabled:', isCreatingMultiSig);
                      handleCreateMultiSignatureWallet();
                    }}
                    className="admin-button success"
                    disabled={isCreatingMultiSig}
                    style={{ position: 'relative', zIndex: 1000 }}
                  >
                    {isCreatingMultiSig ? 'Creating...' : 'Create Multi-Signature Wallet'}
                  </button>
                  <button 
                    onClick={() => setShowMultiSignatureModal(false)}
                    className="admin-button"
                    disabled={isCreatingMultiSig}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Signature Wallet Details Modal */}
      {showViewMultiSigModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="user-details-modal">
              <div className="modal-header">
                <h3>Multi-Signature Wallet Details</h3>
                <button 
                  className="modal-close" 
                  onClick={() => {
                    setShowViewMultiSigModal(false);
                    setSelectedMultiSigWallet(null);
                    setSelectedToken(null);
                  }}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                {loadingMultiSigWallet ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div className="loading-spinner"></div>
                    <p>Loading wallet details...</p>
                  </div>
                ) : selectedMultiSigWallet ? (
                  <div>
                    {/* Wallet Overview */}
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '20px',
                    }}>
                      <h4 style={{ margin: '0 0 12px 0' }}>Wallet Overview</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                        <div>
                          <strong>Wallet Address:</strong>
                          <div style={{ 
                            fontFamily: 'monospace', 
                            fontSize: '12px', 
                            background: '#fff', 
                            padding: '4px 8px', 
                            borderRadius: '4px',
                            marginTop: '4px',
                            wordBreak: 'break-all'
                          }}>
                            {selectedMultiSigWallet.walletAddress}
                          </div>
                        </div>
                        <div>
                          <strong>Status:</strong>
                          <div style={{ 
                            color: selectedMultiSigWallet.status === 'ACTIVE' ? '#28a745' : 
                                   selectedMultiSigWallet.status === 'PENDING' ? '#ffc107' : '#dc3545',
                            fontWeight: '600'
                          }}>
                            {selectedMultiSigWallet.status}
                          </div>
                        </div>
                        <div>
                          <strong>Blockchain:</strong>
                          <div>{selectedMultiSigWallet.blockchain}</div>
                        </div>
                        <div>
                          <strong>Currency:</strong>
                          <div>{selectedMultiSigWallet.currency}</div>
                        </div>
                        <div>
                          <strong>Required Signatures:</strong>
                          <div>{selectedMultiSigWallet.requiredSignatures}/{selectedMultiSigWallet.totalSignatories}</div>
                        </div>
                        <div>
                          <strong>Balance:</strong>
                          <div>${selectedMultiSigWallet.balance?.toLocaleString() || '0'}</div>
                        </div>
                        <div>
                          <strong>Created:</strong>
                          <div>{new Date(selectedMultiSigWallet.createdAt).toLocaleDateString()}</div>
                        </div>
                        <div>
                          <strong>Wallet Type:</strong>
                          <div>{selectedMultiSigWallet.walletType}</div>
                        </div>
                      </div>
                    </div>

                    {/* Escrow Details */}
                    {(selectedMultiSigWallet.escrowTerms || selectedMultiSigWallet.releaseConditions) && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 12px 0' }}>Escrow Details</h4>
                        {selectedMultiSigWallet.escrowTerms && (
                          <div style={{ marginBottom: '12px' }}>
                            <strong>Escrow Terms:</strong>
                            <div style={{ 
                              background: '#fff', 
                              padding: '8px', 
                              borderRadius: '4px', 
                              border: '1px solid #ddd',
                              marginTop: '4px'
                            }}>
                              {selectedMultiSigWallet.escrowTerms || 'No terms specified'}
                            </div>
                          </div>
                        )}
                        {selectedMultiSigWallet.releaseConditions && (
                          <div>
                            <strong>Release Conditions:</strong>
                            <div style={{ 
                              background: '#fff', 
                              padding: '8px', 
                              borderRadius: '4px', 
                              border: '1px solid #ddd',
                              marginTop: '4px'
                            }}>
                              {selectedMultiSigWallet.releaseConditions || 'No conditions specified'}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Associated Property Asset */}
                    {selectedToken && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 12px 0' }}>Associated Property Asset</h4>
                        <div style={{
                          background: '#fff',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #ddd'
                        }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                            <div>
                              <strong>Token Symbol:</strong>
                              <div>{selectedToken.tokenSymbol}</div>
                            </div>
                            <div>
                              <strong>Asset Name:</strong>
                              <div>{selectedToken.assetName}</div>
                            </div>
                            <div>
                              <strong>Asset Value:</strong>
                              <div>${selectedToken.assetValue?.toLocaleString()}</div>
                            </div>
                            <div>
                              <strong>Asset Type:</strong>
                              <div>{selectedToken.assetType}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{ 
                      borderTop: '1px solid #e5e7eb', 
                      paddingTop: '16px',
                      display: 'flex',
                      gap: '12px'
                    }}>
                      {selectedMultiSigWallet.status === 'PENDING' && (
                        <button 
                          className="admin-button success"
                          onClick={() => {
                            // TODO: Implement activate wallet functionality
                            alert('Activate wallet functionality coming soon!');
                          }}
                        >
                          🔓 Activate Wallet
                        </button>
                      )}
                      <button 
                        className="admin-button info"
                        onClick={() => {
                          // Navigate to admin dashboard with multi-signature wallets tab
                          navigate('/admin/dashboard?tab=multiSignatureWallets');
                        }}
                      >
                        📋 Manage Multi-Signature Wallets
                      </button>
                      <button 
                        className="admin-button warning"
                        onClick={() => {
                          handleManageSignatories(selectedMultiSigWallet.id);
                        }}
                      >
                        👥 Manage Signatories
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    <p>No wallet details available.</p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <div className="action-section">
                  <button
                    onClick={() => {
                      setShowViewMultiSigModal(false);
                      setSelectedMultiSigWallet(null);
                      setSelectedToken(null);
                    }}
                    className="action-btn secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Signature Wallet Signatories Modal */}
      {showSignatoriesModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="user-details-modal">
              <div className="modal-header">
                <h3>Multi-Signature Wallet Signatories</h3>
                <button 
                  className="modal-close" 
                  onClick={() => {
                    setShowSignatoriesModal(false);
                    setWalletSignatories([]);
                  }}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                {loadingSignatories ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div className="loading-spinner"></div>
                    <p>Loading signatories...</p>
                  </div>
                ) : walletSignatories.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                    <p>No signatories found for this wallet.</p>
                  </div>
                ) : (
                  <div>
                    {/* Signatories Summary */}
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '20px',
                    }}>
                      <h4 style={{ margin: '0 0 12px 0' }}>Signatories Summary</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                        <div>
                          <strong>Total Signatories:</strong>
                          <div>{walletSignatories.length}</div>
                        </div>
                        <div>
                          <strong>Required Signatures:</strong>
                          <div>{selectedMultiSigWallet?.requiredSignatures || 'N/A'}</div>
                        </div>
                        <div>
                          <strong>Active Signatories:</strong>
                          <div>{walletSignatories.filter(s => s.isActive).length}</div>
                        </div>
                        <div>
                          <strong>Total Signature Weight:</strong>
                          <div>{walletSignatories.reduce((sum, s) => sum + (s.signatureWeight || 0), 0)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Signatories List */}
                    <div>
                      <h4 style={{ margin: '0 0 16px 0' }}>Signatory Details</h4>
                      <div className="signatories-table-container">
                        <table className="admin-table" style={{ 
                          fontSize: '12px', 
                          width: '100%',
                          tableLayout: 'fixed'
                        }}>
                          <thead>
                            <tr>
                              <th style={{ width: '20%' }}>User</th>
                              <th style={{ width: '15%' }}>Type</th>
                              <th style={{ width: '15%' }}>Weight</th>
                              <th style={{ width: '20%' }}>Address</th>
                              <th style={{ width: '10%' }}>Status</th>
                              <th style={{ width: '10%' }}>Added</th>
                              <th style={{ width: '10%' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {walletSignatories.map((signatory) => (
                              <tr key={signatory.id}>
                                <td>
                                  <div className="user-info">
                                    <div className="user-avatar" style={{
                                      backgroundColor: '#e5e7eb',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '10px',
                                      color: '#6b7280',
                                      width: '24px',
                                      height: '24px',
                                      borderRadius: '50%',
                                      marginRight: '8px'
                                    }}>
                                      {signatory.username?.charAt(0) || 'U'}
                                    </div>
                                    <div className="user-details">
                                      <div className="user-name" style={{ fontSize: '11px', fontWeight: '600' }}>
                                        {signatory.username}
                                      </div>
                                      <div className="user-id" style={{ fontSize: '10px', color: '#6b7280' }}>
                                        ID: {signatory.userId}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span style={{
                                    backgroundColor: getSignatoryTypeColor(signatory.signatoryType),
                                    color: '#ffffff',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    textTransform: 'uppercase'
                                  }}>
                                    {signatory.signatoryType}
                                  </span>
                                </td>
                                <td className="numeric-cell">
                                  {signatory.signatureWeight}
                                </td>
                                <td>
                                  <div style={{ 
                                    fontFamily: 'monospace',
                                    fontSize: '10px',
                                    wordBreak: 'break-all',
                                    color: '#6b7280'
                                  }}>
                                    {signatory.signatoryAddress ? 
                                      `${signatory.signatoryAddress.substring(0, 8)}...${signatory.signatoryAddress.substring(signatory.signatoryAddress.length - 6)}` : 
                                      'N/A'
                                    }
                                  </div>
                                </td>
                                <td>
                                  <span style={{
                                    backgroundColor: signatory.isActive ? '#28a745' : '#dc3545',
                                    color: '#ffffff',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}>
                                    {signatory.isActive ? 'ACTIVE' : 'INACTIVE'}
                                  </span>
                                </td>
                                <td className="date-cell">
                                  {signatory.addedAt ? 
                                    new Date(signatory.addedAt).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    }) : 'N/A'
                                  }
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    <button
                                      onClick={() => {
                                        // TODO: Implement edit signatory functionality
                                        alert('Edit signatory functionality coming soon!');
                                      }}
                                      className="admin-button"
                                      style={{ fontSize: '10px', padding: '2px 4px' }}
                                      title="Edit Signatory"
                                    >
                                      ✏️
                                    </button>
                                    {walletSignatories.length > 2 && (
                                      <button
                                        onClick={() => {
                                          // TODO: Implement remove signatory functionality
                                          alert('Remove signatory functionality coming soon!');
                                        }}
                                        className="admin-button danger"
                                        style={{ fontSize: '10px', padding: '2px 4px' }}
                                        title="Remove Signatory"
                                      >
                                        🗑️
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Add New Signatory Section */}
                    <div style={{ 
                      borderTop: '1px solid #e5e7eb', 
                      marginTop: '20px',
                      paddingTop: '16px'
                    }}>
                      <h4 style={{ margin: '0 0 12px 0' }}>Add New Signatory</h4>
                      <button 
                        className="admin-button success"
                        onClick={() => {
                          // TODO: Implement add signatory functionality
                          alert('Add signatory functionality coming soon!');
                        }}
                        style={{ fontSize: '12px' }}
                      >
                        ➕ Add New Signatory
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <div className="action-section">
                  <button
                    onClick={() => {
                      setShowSignatoriesModal(false);
                      setWalletSignatories([]);
                    }}
                    className="action-btn secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signatories Modal */}
      <SignatoriesModal
        isOpen={showSignatoriesModal}
        onClose={() => {
          setShowSignatoriesModal(false);
          setWalletSignatories([]);
        }}
        signatories={walletSignatories}
        loading={loadingSignatories}
        requiredSignatures={selectedMultiSigWallet?.requiredSignatures || 0}
      />
    </div>
  );
};

export default AssetTokens; 